/* legal-gate.js — drives the Terms/DPA acceptance modal.
   Reads window.LEGAL_GATE (set by the layout). Terms are mandatory (no dismiss);
   the DPA can be accepted or deferred. Fetches document text from /legal/document,
   renders it, gates "Accept" on scroll-to-read, and records the outcome.
   LoudLegal.read(type, { fullUrl }) opens the same modal READ-ONLY (no session needed): dismissable,
   plain English on, and a link to the full published text instead of Accept. Used by /register. */
(function () {
  'use strict';

  var gate = window.LEGAL_GATE;

  // Auto sequence from the server gate: mandatory Terms first, then a promptable DPA.
  var autoSteps = [];
  if (gate) {
    if (gate.termsRequired) autoSteps.push('terms');
    if (gate.dpaPromptable) autoSteps.push('dpa');
  }

  var steps = [];
  var scrim, modal, docEl, footEl, headEl, stepIndex = 0, plainOn = false;
  var readOnly = false, fullUrl = '', opener = null;
  // READ TO ACCEPT (LoudLegal.read(type, { onReadEnd })): read-only, but Accept stays grey until the
  // reader reaches the end. Accept closes the modal and calls onReadEnd (the agreement form ticks its box).
  var onReadEnd = null;
  // LIBRARY mode (LoudLegal.library, the account menu's "Legal & agreements"): every document in one modal,
  // a quiet row of tabs to move between them, and what this account accepted in the footer. Cookies is the
  // Privacy Policy's own "Cookies and consent" section (one source of truth, no separate text).
  var library = false, libTab = 'terms', libRecord = null;
  var LIB_TABS = [['terms', 'Terms'], ['dpa', 'DPA'], ['privacy', 'Privacy'], ['cookies', 'Cookies']];
  function libDoc(t) { return t === 'cookies' ? 'privacy' : t; }
  function fmtDay(d) { try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); } catch (e) { return ''; } }
  function recordText(type) {
    if (!libRecord || !libRecord.acceptedAt || (type !== 'terms' && type !== 'dpa')) return '';
    return 'Accepted' + (libRecord.accepter ? ' by ' + libRecord.accepter : '') + (libRecord.entity ? ' for ' + libRecord.entity : '') + ' on ' + fmtDay(libRecord.acceptedAt);
  }
  var docCache = {};

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Markdown-lite: blank line → paragraph, "- " lines → bullet list, "### " → sub-heading.
  function bodyHtml(text) {
    return String(text || '').split(/\n\s*\n/).map(function (block) {
      if (/^### /.test(block)) return '<h4 class="lg-subheading">' + esc(block.slice(4)) + '</h4>';
      var lines = block.split('\n');
      var allBullets = lines.every(function (l) { return /^\s*-\s+/.test(l); });
      if (allBullets) {
        return '<ul>' + lines.map(function (l) {
          return '<li>' + esc(l.replace(/^\s*-\s+/, '')) + '</li>';
        }).join('') + '</ul>';
      }
      return '<p>' + esc(block) + '</p>';
    }).join('');
  }

  function fetchDoc(type) {
    if (docCache[type]) return Promise.resolve(docCache[type]);
    return fetch('/legal/document?type=' + encodeURIComponent(type), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) { docCache[type] = d; return d; });
  }

  function build() {
    scrim = document.createElement('div');
    scrim.className = 'lg-scrim';
    scrim.setAttribute('role', 'dialog');
    scrim.setAttribute('aria-modal', 'true');
    scrim.innerHTML =
      '<div class="lg-modal" role="document">' +
        '<div class="lg-head"></div>' +
        '<div class="lg-doc" tabindex="0"></div>' +
        '<div class="lg-foot"></div>' +
      '</div>';
    document.body.appendChild(scrim);
    modal = scrim.querySelector('.lg-modal');
    headEl = scrim.querySelector('.lg-head');
    docEl = scrim.querySelector('.lg-doc');
    footEl = scrim.querySelector('.lg-foot');

    // Mandatory steps can't be dismissed — swallow scrim clicks + Escape. Read-only closes on both.
    scrim.addEventListener('click', function (e) {
      if (e.target !== scrim) return;
      if (readOnly) close(); else e.stopPropagation();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && scrim && scrim.classList.contains('is-open')) {
        e.preventDefault(); e.stopPropagation();
        if (readOnly) close();
      }
    }, true);

    docEl.addEventListener('scroll', onDocScroll, { passive: true });
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { scrim.classList.add('is-open'); });
  }
  // Opening appears AT SCALE (James 2026-10-21): the width settles (560 → the document's own width) with no growing
  // animation while the modal opens; moving between documents afterwards still eases the width.
  function settleOpen() {
    if (!scrim) return;
    scrim.classList.add('lg-opening');
    clearTimeout(settleOpen.t);
    settleOpen.t = setTimeout(function () { if (scrim) scrim.classList.remove('lg-opening'); }, 700);
  }

  var readGateEnabled = false;
  function onDocScroll() {
    if (!readGateEnabled) return;
    var atEnd = docEl.scrollTop + docEl.clientHeight >= docEl.scrollHeight - 28;
    if (atEnd) {
      docEl.classList.add('is-at-end');
      var accept = footEl.querySelector('[data-accept]');
      if (accept) accept.disabled = false;
      var hint = footEl.querySelector('.lg-read-hint');
      if (hint) hint.classList.add('is-hidden');
    }
  }

  function renderStep() {
    var type = steps[stepIndex];
    fetchDoc(type).then(function (doc) { paint(type, doc); });
  }

  function paint(type, doc) {
    var isTerms = type === 'terms';
    var stepDots = steps.map(function (s, i) {
      var cls = i < stepIndex ? 'is-done' : (i === stepIndex ? 'is-active' : '');
      return '<span class="lg-step-dot ' + cls + '"></span>';
    }).join('');

    var tabs = !library ? '' : '<nav class="lg-tabs" role="tablist" aria-label="Documents">' + LIB_TABS.map(function (t) {
      var on = t[0] === libTab;
      return '<button type="button" class="lg-tab' + (on ? ' is-active' : '') + '" role="tab" aria-selected="' + on + '" data-lib-tab="' + t[0] + '">' + t[1] + '</button>';
    }).join('') + '</nav>';
    headEl.innerHTML = tabs +
      (steps.length > 1 ? '<div class="lg-steps">' + stepDots + '</div>' : '') +
      '<h2 class="lg-title">' + esc(doc.title) + '</h2>' +
      '<p class="lg-meta">Version ' + esc(doc.version) + (doc.effectiveDate ? ' · Effective ' + esc(doc.effectiveDate) : '') + '</p>' +
      (doc.intro ? '<p class="lg-intro">' + esc(doc.intro) + '</p>' : '') +
      // Documents already written in plain English (the Privacy Policy) have no second column.
      (doc.plainEnglish === false ? '' :
      '<div class="lg-plain-toggle' + (plainOn ? ' is-on' : '') + '" data-plain-toggle role="switch" aria-checked="' + plainOn + '">' +
        '<span class="lg-switch"></span> Plain English, side by side' +
      '</div>');

    docEl.innerHTML = doc.sections.map(function (s) {
      // Column order matters: [legal heading, plain heading, legal body, plain body].
      // In plain mode the grid places them row-by-row so headings and bodies align.
      return '<div class="lg-section"><div class="lg-cols">' +
        '<h3 class="lg-section-heading">' + esc(s.heading) + '</h3>' +
        '<h3 class="lg-plain-heading">' + esc(s.plainHeading || 'In plain English') + '</h3>' +
        '<div class="lg-legal">' + bodyHtml(s.legal) + '</div>' +
        '<div class="lg-plain">' + esc(s.plain) + '</div>' +
      '</div></div>';
    }).join('');
    docEl.scrollTop = 0;
    docEl.classList.remove('is-at-end');
    if (doc.plainEnglish === false) plainOn = false;
    applyPlain();

    // Footer differs per document.
    var emailBtn = '<button class="lg-btn lg-btn-ghost" data-email>Email me a copy</button>';
    if (library) {
      var rec = recordText(type);
      footEl.innerHTML =
        '<span class="lg-record">' + esc(rec) + '</span>' +
        '<span class="lg-foot-spacer"></span>' +
        '<button class="lg-btn lg-btn-primary" data-close>Close</button>';
      wireFooter(type, doc);
      footEl.querySelector('[data-close]').addEventListener('click', close);
      headEl.querySelectorAll('[data-lib-tab]').forEach(function (b) {
        b.addEventListener('click', function () {
          var t = b.getAttribute('data-lib-tab');
          if (t === libTab) return;
          libTab = t; steps = [libDoc(t)]; stepIndex = 0; renderStep();
        });
      });
      // Cookies: the Privacy Policy, opened at its cookies section.
      // (Re-aimed once the modal's width has settled: coming from the wide Terms, it narrows over 0.4s and the
      // text reflows, so an early aim lands on the wrong section.)
      if (libTab === 'cookies') {
        var aim = function () {
          if (libTab !== 'cookies') return;
          var secs = docEl.querySelectorAll('.lg-section');
          for (var i = 0; i < secs.length; i++) {
            var h = secs[i].querySelector('.lg-section-heading');
            if (h && /cookie/i.test(h.textContent)) { docEl.scrollTop += secs[i].getBoundingClientRect().top - docEl.getBoundingClientRect().top; break; }
          }
        };
        aim(); requestAnimationFrame(aim); setTimeout(aim, 460);
      }
      docEl.focus({ preventScroll: true });
      return;
    }
    if (readOnly && onReadEnd) {
      footEl.innerHTML =
        '<span class="lg-read-hint"><span>↓</span> Scroll to the end to accept</span>' +
        '<span class="lg-foot-spacer"></span>' +
        '<button class="lg-btn" data-close>Close</button>' +
        '<button class="lg-btn lg-btn-primary" data-accept disabled>Accept</button>';
      wireFooter(type, doc);
      footEl.querySelector('[data-close]').addEventListener('click', close);
      footEl.querySelector('[data-accept]').addEventListener('click', function () {
        var cb = onReadEnd; close(); if (cb) cb(type);
      });
      readGateEnabled = true;
      requestAnimationFrame(function () {
        if (docEl.scrollHeight <= docEl.clientHeight + 28) onDocScroll();
      });
      docEl.focus({ preventScroll: true });
      return;
    }
    if (readOnly) {
      footEl.innerHTML =
        (fullUrl ? '<a class="lg-btn lg-btn-ghost" href="' + esc(fullUrl) + '" target="_blank" rel="noopener">Read the full ' + esc(doc.title) + ' ↗</a>' : '') +
        '<span class="lg-foot-spacer"></span>' +
        '<button class="lg-btn lg-btn-primary" data-close>Close</button>';
      wireFooter(type, doc);
      footEl.querySelector('[data-close]').addEventListener('click', close);
      docEl.focus({ preventScroll: true });
      return;
    }
    if (isTerms) {
      footEl.innerHTML =
        '<span class="lg-read-hint"><span>↓</span> Scroll to read, then accept</span>' +
        '<span class="lg-foot-spacer"></span>' +
        emailBtn +
        '<button class="lg-btn lg-btn-primary" data-accept disabled>Accept &amp; continue</button>';
    } else {
      footEl.innerHTML =
        '<span class="lg-read-hint"><span>↓</span> Scroll to read</span>' +
        '<span class="lg-foot-spacer"></span>' +
        emailBtn +
        '<button class="lg-btn" data-defer>Decide later</button>' +
        '<button class="lg-btn lg-btn-primary" data-accept disabled>Accept</button>' +
        '<p class="lg-email-note">You can use Loud Link now - analytics and fan sign-up unlock once the DPA is accepted.</p>';
    }

    wireFooter(type, doc);

    // Enable the read gate; if the doc already fits without scrolling, unlock immediately.
    readGateEnabled = true;
    requestAnimationFrame(function () {
      if (docEl.scrollHeight <= docEl.clientHeight + 28) {
        docEl.classList.add('is-at-end');
        var a = footEl.querySelector('[data-accept]'); if (a) a.disabled = false;
        var h = footEl.querySelector('.lg-read-hint'); if (h) h.classList.add('is-hidden');
      }
    });
  }

  function applyPlain() {
    modal.classList.toggle('is-plain', plainOn);
    modal.classList.toggle('is-wide', plainOn);
    var t = headEl.querySelector('[data-plain-toggle]');
    if (t) { t.classList.toggle('is-on', plainOn); t.setAttribute('aria-checked', String(plainOn)); }
  }

  function wireFooter(type, doc) {
    var toggle = headEl.querySelector('[data-plain-toggle]');
    if (toggle) toggle.addEventListener('click', function () { plainOn = !plainOn; applyPlain(); });

    var emailBtn = footEl.querySelector('[data-email]');
    if (emailBtn) emailBtn.addEventListener('click', function () {
      emailBtn.disabled = true;
      var orig = emailBtn.textContent;
      emailBtn.textContent = 'Sending…';
      fetch('/legal/email', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document: type })
      }).then(function (r) { return r.json(); })
        .then(function (d) { emailBtn.textContent = d && d.ok ? 'Sent ✓' : 'Try again'; })
        .catch(function () { emailBtn.textContent = 'Try again'; })
        .then(function () { setTimeout(function () { emailBtn.disabled = false; emailBtn.textContent = orig; }, 3500); });
    });

    var acceptBtn = footEl.querySelector('[data-accept]');
    if (acceptBtn) acceptBtn.addEventListener('click', function () {
      setBusy(acceptBtn, 'Saving…');
      post('/legal/accept', { document: type, version: doc.version }).then(next).catch(function () {
        clearBusy(acceptBtn); acceptBtn.textContent = type === 'terms' ? 'Accept & continue' : 'Accept';
      });
    });

    var deferBtn = footEl.querySelector('[data-defer]');
    if (deferBtn) deferBtn.addEventListener('click', function () {
      setBusy(deferBtn, 'One sec…');
      post('/legal/defer', { document: 'dpa', version: doc.version }).then(next).catch(function () {
        clearBusy(deferBtn); deferBtn.textContent = 'Decide later';
      });
    });
  }

  function setBusy(btn, label) { btn.disabled = true; btn.dataset.label = btn.textContent; btn.innerHTML = '<span class="spin"></span>' + label; }
  function clearBusy(btn) { btn.disabled = false; if (btn.dataset.label) btn.textContent = btn.dataset.label; }

  function post(url, body) {
    return fetch(url, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error('bad status'); return r.json(); });
  }

  function next() {
    stepIndex++;
    if (stepIndex < steps.length) { readGateEnabled = false; renderStep(); }
    else finish();
  }

  function finish() {
    document.body.style.overflow = '';
    scrim.classList.remove('is-open');
    // Reload so server-rendered locals + any DPA-gated pages refresh with the new state.
    setTimeout(function () { window.location.reload(); }, 380);
  }

  // Read-only: hand the page back without reloading, and return focus to the link that opened it.
  function close() {
    if (!scrim) return;
    scrim.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () { if (!scrim.classList.contains('is-open')) scrim.style.display = 'none'; }, 450);
    if (opener && opener.focus) opener.focus();
  }

  // Orchestrate a run of one or more steps. Reused by the auto-gate and manual opens.
  function run(list, opts) {
    if (!list || !list.length) return;
    readOnly = !!(opts && opts.readOnly);
    library = !!(opts && opts.library);
    fullUrl = (opts && opts.fullUrl) || '';
    onReadEnd = (opts && typeof opts.onReadEnd === 'function') ? opts.onReadEnd : null;
    opener = readOnly ? document.activeElement : null;
    if (readOnly) plainOn = true;
    steps = list;
    stepIndex = 0;
    readGateEnabled = false;
    if (!scrim) {
      build();
    } else {
      document.body.style.overflow = 'hidden';
      scrim.style.display = '';
      requestAnimationFrame(function () { scrim.classList.add('is-open'); });
    }
    settleOpen();
    renderStep();
  }

  // Public opener so DPA-gated pages can invite acceptance on demand.
  window.LoudLegal = {
    open: function (type) { run([type === 'dpa' ? 'dpa' : 'terms']); },
    // Every document in one modal, tabs to move between them (the account menu's "Legal & agreements").
    library: function (tab) {
      libTab = LIB_TABS.some(function (t) { return t[0] === tab; }) ? tab : 'terms';
      run([libDoc(libTab)], { readOnly: true, library: true });
      fetch('/account/legal/record', { credentials: 'same-origin' }).then(function (r) { return r.json(); })
        .then(function (d) {
          libRecord = d && d.record;
          var el = footEl && footEl.querySelector('.lg-record');
          if (el && library) el.textContent = recordText(libDoc(libTab));
        }).catch(function () {});
    },
    read: function (type, opts) {
      var t = type === 'dpa' || type === 'privacy' || type === 'pixels' ? type : 'terms';
      run([t], { readOnly: true, fullUrl: opts && opts.fullUrl, onReadEnd: opts && opts.onReadEnd });
    }
  };

  function autoStart() { if (autoSteps.length) run(autoSteps); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoStart);
  } else {
    autoStart();
  }
})();
